import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  type Column,
  type Table,
} from '@tanstack/react-table';
import _unescape from 'lodash/unescape';

import { NotificationBadge } from 'features/notification';

import TableFilterComponent from 'shared/components/TableFilterComponent';
import { EntityType } from 'shared/constants';
import useSelector from 'shared/hooks/useSelector';
import AttachIcon from 'shared/icons/Attach';
import {
  createYearFilterFn,
  createStatusOptionFilterFn,
} from 'shared/utils/grid';
import formatDatetime from 'shared/utils/formatDatetime';
import safeParseHtml from 'shared/utils/safeParseHtml';

import { selectAvailableOptions } from '../../selectors';
import type { UserCommonCase } from '../../types';
import { selectCaseLevel1FilterFn } from './helpers';
import { Status } from './UserCommonCasesGrid.styled';

const columnHelper = createColumnHelper<UserCommonCase>();

const useColumns = () => {
  const { t } = useTranslation();

  const options = useSelector(selectAvailableOptions);

  return useMemo(
    () => [
      columnHelper.accessor('Title', {
        header: t('#portal.incident.label.title'),
        cell: (info) => {
          const value = info.getValue();

          return (
            <NotificationBadge
              entityId={info.row.original.Id}
              entityType={EntityType.Incident}
            >
              {value && _unescape(value)}
            </NotificationBadge>
          );
        },
        meta: {
          sx: { width: '10%' },
        },
      }),
      columnHelper.accessor('Space.Name', {
        header: t('##attribute#incident#uds_spaceid'),
        sortingFn: 'alphanumeric',
        meta: {
          sx: { width: '7%' },
        },
      }),
      columnHelper.accessor('CaseLevel1.Label', {
        header: t('#portal.incident.grid.label.uds_requesttype'),
        filterFn: selectCaseLevel1FilterFn,
        meta: {
          sx: { width: '10%' },
          filterComponent: ({
            column,
            table,
          }: {
            column: Column<UserCommonCase, unknown>;
            table: Table<UserCommonCase>;
          }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              showSelectAllOption
              selectAllLabel={'Kaikki tyypit'}
              options={options.caseLevel1}
            />
          ),
        },
      }),
      columnHelper.accessor('Status.Label', {
        header: t('##attribute#incident#statuscode'),
        filterFn: createStatusOptionFilterFn<UserCommonCase>(),
        cell: (info) => {
          const status = info.row.original.Status;
          if (!status) return null;

          return <Status status={status.Value}>{status.Label}</Status>;
        },
        meta: {
          sx: { width: '12%' },
          filterComponent: ({
            column,
            table,
          }: {
            column: Column<UserCommonCase, unknown>;
            table: Table<UserCommonCase>;
          }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              showSelectAllOption
              selectAllLabel={'Kaikki vaiheet'}
              options={options.status}
            />
          ),
        },
      }),
      columnHelper.accessor('Description', {
        header: t('##attribute#incident#uds_portaldescription'),
        cell: (info) => {
          const value = info.getValue();

          return value && safeParseHtml(value);
        },
        meta: {
          sx: { width: '31%' },
        },
      }),
      columnHelper.accessor('HasFiles', {
        header: t('#portal.incident.grid.label.attachments'),
        enableSorting: false,
        cell: (info) =>
          info.getValue() && <AttachIcon sx={{ fontSize: '1.3rem' }} />,
        meta: { align: 'center', sx: { width: '10%' } },
      }),
      columnHelper.accessor('CreatedOn', {
        header: t('##attribute#incident#createdon'),
        cell: (info) => formatDatetime(info.getValue()),
        sortingFn: 'datetime',
        filterFn: createYearFilterFn<UserCommonCase>('CreatedOn'),
        meta: {
          sx: { width: '10%' },
          filterComponent: ({ column, table }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              selectAllLabel={'Vuosi'}
              showSelectAllOption
              options={options.createdOnYear}
            />
          ),
        },
      }),
      columnHelper.accessor('ModifiedOn', {
        header: t('##attribute#incident#modifiedon'),
        enableColumnFilter: false,
        cell: (info) => formatDatetime(info.getValue()),
        sortingFn: 'datetime',
        meta: {
          sx: { width: '10%' },
        },
      }),
    ],
    [options, t]
  );
};

export default useColumns;
